import React from 'react';
import { cn } from '../../lib/utils';
import {
  IoWarningOutline,
  IoCloseCircleOutline,
  IoInformationCircleOutline,
} from 'react-icons/io5';
import { MdErrorOutline } from 'react-icons/md';
import { Button } from './Button';

export interface ErrorAlertProps {
  type?: 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  details?: string;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
  icon?: React.ReactNode;
  actionButton?: {
    text: string;
    onClick?: () => void;
    href?: string;
    variant?: 'primary' | 'secondary';
    external?: boolean;
  };
}

const ErrorAlert: React.FC<ErrorAlertProps> = ({
  type = 'error',
  title,
  message,
  details,
  onDismiss,
  onRetry,
  className,
  icon,
  actionButton,
}) => {
  const typeStyles = {
    error: {
      container: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800',
      icon: 'text-red-500 dark:text-red-400',
      title: 'text-red-900 dark:text-red-100',
      message: 'text-red-700 dark:text-red-200',
      details: 'text-red-600 dark:text-red-300',
      button:
        '!bg-white !text-red-700 hover:!text-red-800 dark:!text-red-300 dark:hover:!text-red-200',
    },
    warning: {
      container: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800',
      icon: 'text-amber-500 dark:text-amber-400',
      title: 'text-amber-900 dark:text-amber-100',
      message: 'text-amber-700 dark:text-amber-200',
      details: 'text-amber-600 dark:text-amber-300',
      button:
        '!bg-white !text-amber-700 hover:!text-amber-800 dark:!text-amber-300 dark:hover:!text-amber-200',
    },
    info: {
      container: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-900 dark:text-blue-100',
      message: 'text-blue-700 dark:text-blue-200',
      details: 'text-blue-600 dark:text-blue-300',
      button:
        '!bg-white !text-blue-700 hover:!text-blue-800 dark:!text-blue-300 dark:hover:!text-blue-200',
    },
  };

  const styles = typeStyles[type];

  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'error':
        return <MdErrorOutline className="w-5 h-5" />;
      case 'warning':
        return <IoWarningOutline className="w-5 h-5" />;
      case 'info':
        return <IoInformationCircleOutline className="w-5 h-5" />;
      default:
        return <MdErrorOutline className="w-5 h-5" />;
    }
  };

  const getDefaultTitle = () => {
    if (title) return title;
    switch (type) {
      case 'error':
        return 'Something went wrong';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      default:
        return 'Error';
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-lg border p-4 transition-all duration-200 animate-in fade-in slide-in-from-top-2',
        styles.container,
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex">
        {/* Icon */}
        <div className={cn('flex-shrink-0', styles.icon)}>{getIcon()}</div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {/* Title */}
          {(title || !details) && (
            <p className={cn('text-sm font-semibold', styles.title)}>{getDefaultTitle()}</p>
          )}

          {/* Message */}
          <div className={cn('text-sm mt-1', styles.message)}>
            <p>{message}</p>
          </div>

          {/* Details (collapsible) */}
          {details && (
            <details className="mt-2">
              <summary className={cn('cursor-pointer text-xs', styles.details)}>
                View details
              </summary>
              <pre className={cn('mt-2 text-xs whitespace-pre-wrap', styles.details)}>
                {details}
              </pre>
            </details>
          )}

          {/* Actions */}
          {(onRetry || actionButton) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {onRetry && (
                <Button
                  onClick={onRetry}
                  size="sm"
                  variant="outline"
                  className={cn('text-xs', styles.button)}
                >
                  Try Again
                </Button>
              )}
              {actionButton && (
                actionButton.href ? (
                  <Button
                    asChild
                    size="sm"
                    variant={actionButton.variant === 'primary' ? 'default' : 'outline'}
                    className={cn(
                      'text-xs',
                      actionButton.variant === 'primary'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                        : styles.button
                    )}
                  >
                    <a
                      href={actionButton.href}
                      target={actionButton.external ? '_blank' : undefined}
                      rel={actionButton.external ? 'noopener noreferrer' : undefined}
                    >
                      {actionButton.text}
                    </a>
                  </Button>
                ) : (
                  <Button
                    onClick={actionButton.onClick}
                    size="sm"
                    variant={actionButton.variant === 'primary' ? 'default' : 'outline'}
                    className={cn(
                      'text-xs',
                      actionButton.variant === 'primary'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                        : styles.button
                    )}
                  >
                    {actionButton.text}
                  </Button>
                )
              )}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              'ml-auto flex-shrink-0 rounded-md p-1.5 inline-flex h-8 w-8 items-center justify-center transition-colors',
              'hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2',
              styles.button
            )}
            aria-label="Dismiss"
          >
            <IoCloseCircleOutline className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Helper function to parse error messages
export const parseErrorMessage = (
  error: unknown
): {
  message: string;
  details?: string;
  type: 'error' | 'warning' | 'info';
} => {
  // Handle ParsedError from error-utils
  if (error && typeof error === 'object' && 'code' in error) {
    const parsedError = error as any;
    
    // Determine type based on error code
    let type: 'error' | 'warning' | 'info' = 'error';
    if (parsedError.code === 'QUOTA_EXCEEDED') {
      type = 'warning';
    } else if (parsedError.code === 'INVALID_INPUT') {
      type = 'warning';
    }
    
    return {
      message: parsedError.message || 'An error occurred',
      details: parsedError.details ? JSON.stringify(parsedError.details, null, 2) : undefined,
      type,
    };
  }
  
  if (typeof error === 'string') {
    // Check for specific error patterns
    if (error.toLowerCase().includes('quota') || error.toLowerCase().includes('limit')) {
      return {
        message: 'You have reached your usage limit for today.',
        details: error,
        type: 'warning',
      };
    }
    if (error.toLowerCase().includes('network') || error.toLowerCase().includes('connection')) {
      return {
        message: 'Connection error. Please check your internet and try again.',
        details: error,
        type: 'error',
      };
    }
    if (error.toLowerCase().includes('invalid') || error.toLowerCase().includes('format')) {
      return {
        message: 'Invalid input format. Please check and try again.',
        details: error,
        type: 'warning',
      };
    }
    return {
      message: error,
      type: 'error',
    };
  }

  if (error instanceof Error) {
    // Check error message for patterns
    const message = error.message.toLowerCase();
    let type: 'error' | 'warning' | 'info' = 'error';
    
    if (message.includes('quota') || message.includes('limit')) {
      type = 'warning';
    }
    
    return {
      message: error.message,
      details: error.stack,
      type,
    };
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as any;
    
    // Check for error.error structure (API response)
    if (errorObj.error && typeof errorObj.error === 'object') {
      return {
        message: errorObj.error.message || errorObj.message || 'An error occurred',
        details: JSON.stringify(errorObj.error.details || errorObj, null, 2),
        type: errorObj.error.code === 'QUOTA_EXCEEDED' ? 'warning' : 'error',
      };
    }
    
    return {
      message: String(errorObj.message),
      details: JSON.stringify(errorObj, null, 2),
      type: 'error',
    };
  }

  return {
    message: 'An unexpected error occurred. Please try again.',
    type: 'error',
  };
};

export default ErrorAlert;
